/**
 * marketing/api.ts
 * --------------------------------------------------------------
 * Typed client for the public site-settings endpoints
 * (`/api/public/site/*`). These endpoints require no auth and
 * power the marketing pages (reviews, blog/changelog, SEO).
 *
 * All fetches go through this module — pages must never call
 * `fetch` directly. Every function is defensive: if the backend
 * is offline or returns a non-OK response, callers receive a
 * sensible empty/null fallback so pages can keep rendering.
 *
 * Endpoint contract is sourced from
 *   api/modules/site-settings/routes.ts → sitePublicRouter()
 *   api/modules/site-settings/service.ts (list/get helpers)
 */

import { api } from '../core/api-client';

/* ──────────────────────────────────────────────────────────────
 * Types — mirror api/modules/site-settings/types.ts
 * ──────────────────────────────────────────────────────────── */

export interface Review {
  id: string;
  name: string;
  role: string | null;
  company: string | null;
  sector: string | null;
  city: string | null;
  rating: number | null;
  body: string;
  avatar_url: string | null;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string;
  cover_url: string | null;
  author_name: string | null;
  tags: string[];
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SeoSettings {
  default_title: string;
  default_description: string;
  default_og_image_url: string | null;
  default_twitter_handle: string | null;
  default_robots: string;
  sitemap_enabled: boolean;
  indexable: boolean;
  gtm_id: string | null;
  ga4_id: string | null;
  updated_at: string;
}

/* ──────────────────────────────────────────────────────────────
 * Defaults — used when backend returns nothing usable
 * ──────────────────────────────────────────────────────────── */

export const DEFAULT_SEO: SeoSettings = {
  default_title: 'Worqly — the operating system for small business',
  default_description:
    'Send invoices, run payroll, track expenses and time. Modular business platform — pay for the modules you use and the employees you actually track.',
  default_og_image_url: null,
  default_twitter_handle: null,
  default_robots: 'index, follow',
  sitemap_enabled: true,
  indexable: true,
  gtm_id: null,
  ga4_id: null,
  updated_at: '',
};

/* ──────────────────────────────────────────────────────────────
 * Low-level fetch (no auth, never throws on network errors)
 * ──────────────────────────────────────────────────────────── */

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(path, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const text = await res.text();
    if (!text) return null;
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

/* ──────────────────────────────────────────────────────────────
 * Public client
 * ──────────────────────────────────────────────────────────── */

type ListReviewsOpts = {
  /** Hard cap on results returned. */
  limit?: number;
  /**
   * Sort order:
   *   'default' (default): preserve backend ordering (sort_order ASC, then created_at DESC)
   *   'newest':  override to created_at DESC
   *   'rating':  highest rating first, NULLs last
   */
  sort?: 'default' | 'newest' | 'rating';
};

export async function listReviews(opts: ListReviewsOpts = {}): Promise<Review[]> {
  const data = await getJson<{ reviews?: Review[] }>('/api/public/site/reviews');
  let reviews = Array.isArray(data?.reviews) ? data!.reviews! : [];

  if (opts.sort === 'newest') {
    reviews = [...reviews].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  } else if (opts.sort === 'rating') {
    reviews = [...reviews].sort((a, b) => {
      const ar = a.rating ?? -1;
      const br = b.rating ?? -1;
      return br - ar;
    });
  }

  if (typeof opts.limit === 'number' && opts.limit >= 0) {
    reviews = reviews.slice(0, opts.limit);
  }
  return reviews;
}

type ListBlogPostsOpts = {
  /** Hard cap on results returned. */
  limit?: number;
};

export async function listBlogPosts(opts: ListBlogPostsOpts = {}): Promise<BlogPost[]> {
  const data = await getJson<{ posts?: BlogPost[] }>('/api/public/site/blog');
  let posts = Array.isArray(data?.posts) ? data!.posts! : [];

  // Backend already filters to published + sorts published_at DESC, but be defensive.
  posts = posts.filter((p) => p.is_published);
  posts.sort((a, b) => {
    const at = a.published_at ?? a.created_at;
    const bt = b.published_at ?? b.created_at;
    return new Date(bt).getTime() - new Date(at).getTime();
  });

  if (typeof opts.limit === 'number' && opts.limit >= 0) {
    posts = posts.slice(0, opts.limit);
  }
  return posts;
}

export async function getBlogPost(slug: string): Promise<BlogPost | null> {
  if (!slug) return null;
  const data = await getJson<{ post?: BlogPost }>(
    `/api/public/site/blog/${encodeURIComponent(slug)}`,
  );
  return data?.post ?? null;
}

export interface PublicHelpDoc {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string;
  category: string;
  sort_order: number;
  seo_title: string | null;
  seo_description: string | null;
  published_at: string | null;
}

export async function getHelpDocs(): Promise<PublicHelpDoc[]> {
  const r = await getJson<{ docs: PublicHelpDoc[] }>('/api/public/site/docs');
  return r?.docs ?? [];
}

export async function getHelpDoc(slug: string): Promise<PublicHelpDoc | null> {
  const r = await getJson<{ doc: PublicHelpDoc }>(`/api/public/site/docs/${encodeURIComponent(slug)}`);
  return r?.doc ?? null;
}

/* ──────────────────────────────────────────────────────────────
 * Module pricing — live from /api/public/site/prices
 * (module_prices table, editable in the super-admin panel).
 * Marketing pages keep hardcoded fallbacks so they still render
 * if the backend is unreachable; live values win when available.
 * ──────────────────────────────────────────────────────────── */

export interface PublicModulePrice {
  module_key: string;
  currency: string;
  amount_per_month: number;
  seat_amount_per_month: number;
  /**
   * Seats bundled into the base price before the per-seat rate kicks in
   * (prepaid-billing revamp, 2026-07-29). Payroll is the first module to use
   * this: base includes 10 employees, then a per-employee rate for the rest.
   * Always 0 for modules with no allowance (hr, monitoring — every seat is
   * billed from the first one).
   */
  included_seats: number;
}

export type PriceMap = Record<string, PublicModulePrice>;

/** Pure mapper: raw rows → keyed map, dropping malformed entries. Exported for tests. */
export function buildPriceMap(rows: unknown): PriceMap | null {
  if (!Array.isArray(rows)) return null;
  const map: PriceMap = {};
  for (const raw of rows) {
    const r = raw as Partial<PublicModulePrice>;
    const base = Number(r?.amount_per_month);
    const seat = Number(r?.seat_amount_per_month ?? 0);
    const included = Number(r?.included_seats ?? 0);
    if (!r?.module_key || !Number.isFinite(base) || !Number.isFinite(seat)) continue;
    map[r.module_key] = {
      module_key: r.module_key,
      currency: r.currency ?? 'USD',
      amount_per_month: base,
      seat_amount_per_month: seat,
      included_seats: Number.isFinite(included) && included > 0 ? included : 0,
    };
  }
  return Object.keys(map).length > 0 ? map : null;
}

// One fetch per page load is plenty — the pricing + landing sections each call
// this, so memoize the in-flight promise for the session.
let pricesPromise: Promise<PriceMap | null> | null = null;

export async function getPublicPrices(): Promise<PriceMap | null> {
  if (!pricesPromise) {
    pricesPromise = getJson<{ prices?: unknown }>('/api/public/site/prices')
      .then((data) => buildPriceMap(data?.prices))
      .catch(() => null);
  }
  return pricesPromise;
}

export async function getSeoSettings(): Promise<SeoSettings> {
  const data = await getJson<{ seo?: SeoSettings }>('/api/public/site/seo');
  const seo = data?.seo;
  if (!seo) return { ...DEFAULT_SEO };
  // Fill any missing string fields from defaults so renderers always have something.
  return {
    default_title: seo.default_title?.trim() || DEFAULT_SEO.default_title,
    default_description: seo.default_description?.trim() || DEFAULT_SEO.default_description,
    default_og_image_url: seo.default_og_image_url ?? null,
    default_twitter_handle: seo.default_twitter_handle ?? null,
    default_robots: seo.default_robots?.trim() || DEFAULT_SEO.default_robots,
    sitemap_enabled: seo.sitemap_enabled ?? true,
    indexable: seo.indexable ?? true,
    gtm_id: seo.gtm_id ?? null,
    ga4_id: seo.ga4_id ?? null,
    updated_at: seo.updated_at ?? '',
  };
}

/* ──────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────── */

/**
 * Convert a company name (or any string) to a URL-safe slug.
 * Mirrors the SLUG_RE in the backend (lowercase, digits, dashes).
 */
export function slugifyCompany(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

/** Build the customer detail slug for a review. Prefers company, falls back to name. */
export function reviewSlug(r: Pick<Review, 'company' | 'name'>): string {
  return slugifyCompany(r.company || r.name);
}

/* ──────────────────────────────────────────────────────────────
 * Public contact form → POST /api/public/contact
 * ──────────────────────────────────────────────────────────── */

export async function submitContact(input: {
  name: string; email: string; subject: string; topic: string; message: string; turnstileToken?: string;
}): Promise<{ ok: true; ticketId: string }> {
  return api<{ ok: true; ticketId: string }>('/api/public/contact', {
    method: 'POST', noRedirectOn401: true, body: input,
  });
}
